import styled, { css } from 'styled-components';
import { inPixels, opacify, px2rem } from '../utils/helpers/style.js';
import { styles } from '../constants/styles.js';
import { boxStyles } from '../Box/styles.js';
import { fontFamilyStyles } from '../ContentText/styles.web.js';
import { boxPropExclusions } from '../Box/props.js';
import { themeVariant } from '../utils/helpers/themeVariant.web.js';

var inputHasValue = /*#__PURE__*/css(["padding-bottom:", ";padding-top:", ";"], themeVariant({
  normal: function normal(p) {
    return p.theme.inputField.web.sizings[p.$fieldSize].paddings.input.bottom;
  },
  condensed: function condensed(p) {
    return p.theme.inputField.web.sizings.s.paddings.input.bottom;
  }
}), themeVariant({
  normal: function normal(p) {
    return p.theme.inputField.web.sizings[p.$fieldSize].paddings.input.top;
  },
  condensed: function condensed(p) {
    return p.theme.inputField.web.sizings.s.paddings.input.top;
  }
}));
var labelIsAnimated = /*#__PURE__*/css(["font-size:", ";transform:translateY( -", " );padding-top:", ";"], function (p) {
  return p.theme.inputField.web.sizings[p.$fieldSize].label.activeFontSize;
}, function (p) {
  return p.theme.inputField.web.sizings[p.$fieldSize].label.activeFontSize;
}, function (props) {
  return props.theme.inputField.web.sizings[props.$fieldSize].paddings.label.activeTop;
});
var iconStyles = /*#__PURE__*/css(["color:", ";background:transparent;position:absolute;top:", ";border:0;z-index:5;padding:0;svg{transition:color ", ";transition-timing-function:", ";}transition:opacity ", ";transition-timing-function:", ";"], function (p) {
  return p.theme.inputField.web.inactive;
}, function (p) {
  return "calc(50% - ".concat(inPixels(p.theme.inputField.web.sizings[p.$fieldSize].icons.size / 2), ")");
}, styles.transition.timing.slowest, styles.transition["function"].base, styles.transition.timing.slowest, styles.transition["function"].base);
var leftIcon = /*#__PURE__*/styled.button.withConfig({
  displayName: "styles__leftIcon",
  componentId: "sc-1eq1lk4-0"
})(["", ";left:", ";"], iconStyles, function (p) {
  return inPixels(p.theme.inputField.web.sizings[p.$fieldSize].icons.paddingHorizontal);
});
var rightIcon = /*#__PURE__*/styled.button.withConfig({
  displayName: "styles__rightIcon",
  componentId: "sc-1eq1lk4-1"
})(["", ";right:", ";"], iconStyles, function (p) {
  return inPixels(p.theme.inputField.web.sizings[p.$fieldSize].icons.paddingHorizontal);
});
var inputFieldBorderMapping = {
  top: ['full', 'bottomHidden'],
  left: ['full', 'bottomHidden'],
  right: ['full', 'bottomHidden'],
  bottom: ['full', 'bottomOnly']
};
var borderActive = function borderActive(props) {
  return function (border) {
    return inputFieldBorderMapping[border].includes(props.$borderStyle);
  };
};
var pickIconLeftPadding = function pickIconLeftPadding(props) {
  return inPixels(props.$withLeftIcon ? props.theme.inputField.web.sizings[props.$fieldSize].paddings.input.horizontalWithIcon : props.theme.inputField.web.sizings[props.$fieldSize].paddings.input.horizontalDefault);
};
var pickIconRightPadding = function pickIconRightPadding(props) {
  return inPixels(props.$withRightIcon ? props.theme.inputField.web.sizings[props.$fieldSize].paddings.input.horizontalWithIcon : props.theme.inputField.web.sizings[props.$fieldSize].paddings.input.horizontalDefault);
};
var label = /*#__PURE__*/styled.label.withConfig({
  displayName: "styles__label",
  componentId: "sc-1eq1lk4-2"
})(["", ";line-height:", ";text-align:left;color:", ";font-size:", ";font-weight:", ";left:0;overflow:hidden;padding-left:", ";padding-right:", ";padding-top:", ";padding-bottom:", ";pointer-events:none;position:absolute;top:0;right:0;text-overflow:ellipsis;transition:color ", ",font-size ", ",transform ", ",padding ", ";transition-timing-function:", ";white-space:nowrap;"], fontFamilyStyles, themeVariant({
  normal: function normal(p) {
    return p.theme.inputField.web.sizings[p.$fieldSize].label.lineHeight;
  },
  condensed: function condensed(p) {
    return p.theme.inputField.web.sizings.s.label.lineHeight;
  }
}), function (p) {
  return p.$transparent ? p.theme.inputField.web.transparent.label : p.theme.inputField.web.textColor;
}, themeVariant({
  normal: function normal(p) {
    return p.theme.inputField.web.sizings[p.$fieldSize].label.fontSize;
  },
  condensed: function condensed(p) {
    return p.theme.inputField.web.sizings.s.label.fontSize;
  }
}), function (props) {
  return props.theme.inputField.web.label.fontWeight;
}, pickIconLeftPadding, pickIconRightPadding, function (props) {
  return props.theme.inputField.web.sizings[props.$fieldSize].paddings.label.top;
}, function (props) {
  return props.theme.inputField.web.sizings[props.$fieldSize].paddings.label.bottom;
}, styles.transition.timing.slower, styles.transition.timing.slower, styles.transition.timing.slower, styles.transition.timing.slower, styles.transition["function"].base);
var resolveBorderRemoval = function resolveBorderRemoval(border) {
  return function (props) {
    return borderActive(props)(border) ? null : 0;
  };
};
var getTransparentBorderColor = function getTransparentBorderColor(props, active) {
  return opacify(props.theme.inputField.web.transparent.border, active ? props.theme.inputField.web.transparent.activeOpacity : props.theme.inputField.web.transparent.inactiveOpacity);
};
var getTransparentTextColor = function getTransparentTextColor(props, active) {
  return opacify(props.theme.inputField.web.transparent.text, props.theme.inputField.web.transparent.inactiveOpacity);
};
var input = /*#__PURE__*/styled.input.withConfig({
  displayName: "styles__input",
  componentId: "sc-1eq1lk4-3"
})(["", ";box-sizing:border-box;outline:none;-webkit-appearance:none;background:", ";border-color:", ";border-style:solid;border-width:", ";border-top-left-radius:", ";border-top-right-radius:", ";border-bottom-left-radius:", ";border-bottom-right-radius:", ";border-top:", ";border-left:", ";border-right:", ";border-bottom:", ";box-shadow:none;color:", ";font-size:", ";height:", ";padding-left:", ";padding-right:", ";", " transition-timing-function:", ";width:100%;+ ", "{display:", ";}&::placeholder{color:", ";opacity:", ";transition:opacity ", ";transition-timing-function:", ";}&:-ms-input-placeholder{color:rgba(0 0 0 0%);opacity:1;}&[type='search']::-webkit-search-cancel-button{display:none;}&:enabled:hover{", "}&:not([value='']),&[type='date'],&[type='datetime-local'],&[type='month'],&[type='time'],&[type='week']{", ";+ ", "{color:", ";", "}}&:disabled{-webkit-text-fill-color:currentColor;opacity:1;background:", ";color:", ";border-color:", ";cursor:not-allowed;+ ", "{color:", ";}~ ", ",~ ", "{color:", ";}}", " &::-ms-clear{display:none;}"], fontFamilyStyles, function (p) {
  return p.$transparent ? opacify(p.theme.inputField.web.background, 0) : p.theme.inputField.web.background;
}, function (p) {
  return p.$transparent ? getTransparentBorderColor(p) : p.theme.inputField.web.inactive;
}, px2rem(2), function (props) {
  return props.$borderRadius;
}, function (props) {
  return props.$borderRadius;
}, function (props) {
  return !props.$disableBottomBorderRadius ? props.$borderRadius : 0;
}, function (props) {
  return !props.$disableBottomBorderRadius ? props.$borderRadius : 0;
}, resolveBorderRemoval('top'), resolveBorderRemoval('left'), resolveBorderRemoval('right'), function (props) {
  return props.$disableBottomBorder ? 0 : resolveBorderRemoval('bottom');
}, function (p) {
  return p.$transparent ? p.theme.inputField.web.transparent.text : p.theme.inputField.web.textColor;
}, themeVariant({
  normal: function normal(p) {
    return p.theme.inputField.web.sizings[p.$fieldSize].fontSize;
  },
  condensed: function condensed(p) {
    return p.theme.inputField.web.sizings.s.fontSize;
  }
}), themeVariant({
  normal: function normal(p) {
    return p.theme.inputField.web.sizings[p.$fieldSize].height;
  },
  condensed: function condensed(p) {
    return p.theme.inputField.web.sizings.s.height;
  }
}), function (props) {
  return borderActive(props)('left') ? inPixels(props.$withLeftIcon ? props.theme.inputField.web.sizings[props.$fieldSize].paddings.input.horizontalWithIcon - 2 : props.theme.inputField.web.sizings[props.$fieldSize].paddings.input.horizontalDefault - 2) : pickIconLeftPadding(props);
}, function (props) {
  return borderActive(props)('right') ? inPixels(props.$withRightIcon ? props.theme.inputField.web.sizings[props.$fieldSize].paddings.input.horizontalWithIcon - 2 : props.theme.inputField.web.sizings[props.$fieldSize].paddings.input.horizontalDefault - 2) : pickIconRightPadding(props);
}, function (props) {
  return props.readOnly === undefined ? css(["transition:background ", ",border-color ", ";"], styles.transition.timing.slower, styles.transition.timing.slowest) : css(["transition:background ", ",padding-left ", ",border-color ", " ", ";"], styles.transition.timing.slower, styles.transition.timing.slower, styles.transition.timing.slowest, props.readOnly ? '0s' : styles.transition.timing.faster);
}, styles.transition["function"].base, label, function (props) {
  return props.$labelStyle === 'floating' ? 'block' : 'none';
}, function (p) {
  return p.theme.inputField.web.placeholder;
}, function (props) {
  return props.$labelStyle === 'floating' ? 0 : 1;
}, styles.transition.timing.slowest, styles.transition["function"].base, function (props) {
  return css(["border-color:", ";~ ", ",~ ", "{color:", ";}"], props.$transparent ? getTransparentBorderColor(props, true) : props.$color, leftIcon, rightIcon, !props.readOnly ? props.$transparent ? opacify(props.theme.inputField.web.transparent.text, props.theme.inputField.web.transparent.activeOpacity) : props.$color : undefined);
}, function (props) {
  return props.$labelStyle === 'floating' && inputHasValue;
}, label, function (p) {
  return p.$transparent ? p.theme.inputField.web.transparent.text : p.theme.inputField.web.label.color;
}, labelIsAnimated, themeVariant({
  normal: function normal(p) {
    return p.$transparent ? 'transparent' : p.theme.inputField.web.disabled.background;
  }
}), function (p) {
  return p.theme.inputField.web.disabled.color;
}, function (p) {
  return p.$transparent ? getTransparentBorderColor(p, false) : p.theme.inputField.web.disabled.border;
}, label, function (p) {
  return p.$transparent ? getTransparentTextColor(p) : p.theme.inputField.web.disabled.label;
}, leftIcon, rightIcon, function (p) {
  return p.$transparent ? getTransparentTextColor(p) : p.theme.inputField.web.disabled.label;
}, function (props) {
  var borderColorOnFocus = props.$transparent ? props.theme.inputField.web.transparent.border : props.$color;
  return props.readOnly ? css(["-webkit-text-fill-color:currentColor;opacity:1;background:transparent;border-color:transparent;&:enabled:hover{background:transparent;border-color:transparent;}text-overflow:ellipsis;", " + ", "{color:", ";", "}"], props.$labelStyle === 'floating' && inputHasValue, label, function (p) {
    return p.theme.inputField.web.label.color;
  }, labelIsAnimated) : css(["&:enabled:focus{transition:border-radius ", ",background-color ", ";transition-timing-function:", ";color:", ";border-color:", ";border-left:", " solid ", ";border-right:", " solid ", ";border-top:", " solid ", ";background:", ";border-bottom:", ";border-top-left-radius:", ";border-top-right-radius:", ";border-bottom-left-radius:", ";border-bottom-right-radius:", ";", " &::placeholder{opacity:1;}+ ", "{", ";color:", ";}~ ", ",~ ", "{color:", ";}}"], styles.transition.timing.slower, styles.transition.timing.slower, styles.transition["function"].base, props.theme.inputField.web.textColor, borderColorOnFocus, px2rem(2), borderColorOnFocus, px2rem(2), borderColorOnFocus, px2rem(2), borderColorOnFocus, function (p) {
    return p.theme.inputField.web.background;
  }, function (props) {
    return props.$borderStyle !== 'bottomHidden' && !props.$disableBottomBorder ? "".concat(px2rem(2), " solid ").concat(borderColorOnFocus, ";;") : undefined;
  }, function (props) {
    return props.$borderRadius;
  }, function (props) {
    return props.$borderRadius;
  }, function (props) {
    return !props.$disableBottomBorder && props.$borderStyle !== 'bottomHidden' && props.$disableBottomBorderRadius || !props.$disableBottomBorderRadius && props.$borderStyle === 'bottomOnly' ? props.$borderRadius : undefined;
  }, function (props) {
    return !props.$disableBottomBorder && props.$borderStyle !== 'bottomHidden' && props.$disableBottomBorderRadius || !props.$disableBottomBorderRadius && props.$borderStyle === 'bottomOnly' ? props.$borderRadius : undefined;
  }, props.$labelStyle === 'floating' && inputHasValue, label, labelIsAnimated, props.$color, leftIcon, rightIcon, props.$color);
});
var field = /*#__PURE__*/styled.div.withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return !boxPropExclusions.has(prop);
  }
}).withConfig({
  displayName: "styles__field",
  componentId: "sc-1eq1lk4-4"
})(["position:relative;width:100%;", " ", " ", ""], function (props) {
  if (props.$transparent) {
    return css(["", ",", "{color:", ";}"], leftIcon, rightIcon, getTransparentTextColor(props));
  }
}, function (props) {
  if (props.$transparent && props.$error) {
    return css(["", "{background-color:", ";&:not(:focus){color:", ";}}", "{color:", ";&:not([value='']){color:", ";}}"], input, props.theme.inputField.web.background, props.$errorColor, label, props.$errorColor, props.$errorColor);
  }
  if (props.$error) {
    return css(["", "{&:not(:focus){border-color:", ";~ ", ",~ ", "{color:", ";}}}"], input, props.$errorColor, leftIcon, rightIcon, props.$errorColor);
  }
  return null;
}, boxStyles);

export { field, input, label, leftIcon, rightIcon };
